import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DAppLayout from './layouts/DAppLayout'
import {
  useUserByWalletInRouter as useUserByWalletFromRouter,
  useUserStats,
  useWalletFromRouter,
} from '../techRaffles/hooks/user'
import {
  useColorMode,
  Badge,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Link,
  Skeleton,
  SkeletonCircle,
  Stack,
  Text,
  Tooltip,
  VStack,
  Container,
  Wrap,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react'
import { FaDiscord, FaTwitter } from 'react-icons/fa'
import FilterButtons from './components/FilterButtons'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  useRafflesSavedByUser,
  useUserRaffles,
} from '../techRaffles/hooks/raffle'
import { DiscordLinkMultiButton } from '../admin/components/discord-link-ui/DiscordLinkMultiButton'
import { TwitterLinkMultiButton } from '../admin/components/twitter-link-ui/TwitterLinkMultiButton'
import { useProjectsWhereUserIsHolderFromRouter } from '../techRaffles/hooks/project'
import { ProfilePicture } from './components/ProfilePicture'
import { useS3Upload } from 'next-s3-upload'
import { trpc } from '../../utils/trpc'
import toast from 'react-hot-toast'
import { formatFloatForDisplay, getParsedFile } from '../../utils/utils'
import { uploadFileToS3Hook } from '../../utils/s3'
import InfiniteScroll from 'react-infinite-scroll-component'
import RaffleCardList from './components/RaffleCardList'
import RaffleCardListSkeleton from './components/Skeletons/RaffleCardListSkeleton'
import {
  raffleMinType,
  raffleType,
  raffleUserConnectionType,
  userSimpleType,
  userType,
} from '../techRaffles/types'
import { LoadingMoreRaffles } from './components/LoadingMoreRaffles'
import { useLocation } from 'react-use'

import { PiLightning, PiStackSimple, PiUsers, PiStar } from 'react-icons/pi'
import { useIsUserPlatformAdmin } from '../common/auth/authHooks'
import { BsFillPatchCheckFill } from 'react-icons/bs'
import { CommunityBox } from './components/CommunityBox'
import { FormInputWithMode } from '../../components/Forms/FormInputWithMode'
import { WalletSignInModal } from './components/WalletSignInModal'
import { useUser } from '../common/auth/authHooks'
import { useWalletSignInModal } from '../../components/wallet-ui/useWalletSignInModal'

import { StatCard } from '../rewards/components/StatCard'
import { ContentTabs } from '../rewards/components/ContentTabs'
import { ProfileCard } from '../rewards/components/ProfileCard'
import { EditModal } from '../rewards/components/EditModal'
import Header from './layouts/Header'
import Footer from './layouts/Footer'
import { IoSwapHorizontalOutline } from 'react-icons/io5'

import CoinSwapIcon from '../../../public/images/rewards/coinSwapIcon.svg'
import { HiOutlineTicket } from 'react-icons/hi'
import { BiCoinStack, BiParty } from 'react-icons/bi'
import WeeklyRewards from '../rewards/screens/WeeklyRewards'
import MyRewards from '../rewards/screens/MyRewards'
import PastRewards from '../rewards/screens/PastRewards'

const raffleTypeLabels: { label: string; value: raffleUserConnectionType }[] = [
  { label: 'All', value: 'ALL' },
  {
    label: 'Created',
    value: 'CREATED',
  },
  { label: 'Participated', value: 'PARTICIPATED' },
  { label: 'Won', value: 'WON' },
]

export const UserProfileStats = () => {
  const walletFromRouter = useWalletFromRouter()
  const userProfileStatsRes = useUserStats(walletFromRouter)

  return (
    <Box w='100%'>
      <SimpleGrid
        columns={[2, 4, 8]}
        justifyContent={'center'}
        mb='50px'
        spacing='3'
      >
        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={PiLightning}
            path='/images/rewards/coinSwapIcon.svg'
            title='Raffle Volume'
            number={`${formatFloatForDisplay(
              userProfileStatsRes.data?.raffleStats?.totalVolume
            )} SOL`}
            variant='user'
          />
        </Flex>

        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={PiLightning}
            title='Raffles Created'
            number={userProfileStatsRes.data?.raffleStats?.rafflesCreated}
            variant='user'
          />
        </Flex>
        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            variant='user'
            Icon={HiOutlineTicket}
            title='Tickets Sold'
            number={userProfileStatsRes.data?.raffleStats?.ticketsSold}
          />
        </Flex>
        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={PiStackSimple}
            title='Raffles Bought'
            number={userProfileStatsRes.data?.buyingStats?.participatedCount}
            variant='user'
          />
        </Flex>

        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={BiParty}
            title='Raffles Won'
            number={userProfileStatsRes.data?.winningStats?.wonRaffles}
            variant='user'
          />
        </Flex>

        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={BiCoinStack}
            title='Buy Volume'
            number={`${formatFloatForDisplay(
              userProfileStatsRes.data?.buyingStats?.totalVolume
            )} SOL`}
            variant='user'
          />
        </Flex>

        <Flex justifyContent='center' alignItems='center'>
          <StatCard
            Icon={PiStackSimple}
            title='Tickets bought'
            number={userProfileStatsRes.data?.buyingStats?.ticketsBought}
            variant='user'
          />
        </Flex>
      </SimpleGrid>
    </Box>
  )
}

type rewardSectionTypes = 'ALL' | 'PARTICIPATED' | 'PAST'

const rewardSectionLabels: { label: string; value: rewardSectionTypes }[] = [
  {
    value: 'ALL',
    label: 'All',
  },
  {
    value: 'PARTICIPATED',
    label: 'My entries',
  },
  {
    value: 'PAST',
    label: 'Past',
  },
]

export const UserRewardsOverview: React.FC<{ user?: userType }> = ({
  user,
}) => {
  const [rewardSectionType, setRewardSectionType] = useState('ALL')

  return (
    <Box w='100%'>
      <FilterButtons
        selectedValue={rewardSectionType}
        onChange={(it) => setRewardSectionType(it as rewardSectionTypes)}
        labels={rewardSectionLabels}
      />
      {rewardSectionType === 'ALL' && <WeeklyRewards userId={user?.id} />}
      {rewardSectionType === 'PARTICIPATED' && <MyRewards userId={user?.id} />}
      {rewardSectionType === 'PAST' && <PastRewards userId={user?.id} />}
    </Box>
  )
}

export const DAppProfileOverview = () => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const { publicKey: userPubKey } = useWallet()
  const connectedWallet = useMemo(() => {
    return userPubKey?.toBase58()
  }, [userPubKey])
  const walletFromRouter = useWalletFromRouter()
  const { data: user, refetch } = useUserByWalletFromRouter()
  const userProfileStatsRes = useUserStats(walletFromRouter)
  const userCommunitiesRes = useProjectsWhereUserIsHolderFromRouter()
  const { uploadToS3 } = useS3Upload()

  const [editModal, setEditModal] = useState(false)

  const loading = useMemo(() => {
    return !user
  }, [user])

  const isOwnProfile = useMemo(
    () => user?.wallet === connectedWallet && !!user?.wallet,
    [user]
  )

  useEffect(() => {
    if (isOwnProfile && !raffleTypeLabels.some((it) => it.value === 'SAVED')) {
      raffleTypeLabels.push({ label: 'Saved', value: 'SAVED' })
      raffleTypeLabels.push({ label: 'Cancelled', value: 'CANCELLED' })
    }
  }, [isOwnProfile])

  const savedByUserRes = useRafflesSavedByUser(isOwnProfile)

  const [raffleType, setPRaffleType] = useState<raffleUserConnectionType>('ALL')
  const [page, setPage] = useState<number>(0)
  const [raffles, setRaffles] = useState<raffleMinType[]>([])
  const [hasMore, setHasMore] = useState(true)

  const { isAuthed } = useUser()
  const location = useLocation()
  const setRaffleType = (it: raffleUserConnectionType) => {
    setPRaffleType(it)
    history.replaceState(null, '', '#' + it)
  }
  useEffect(() => {
    setTimeout(() => {
      const queryCategoryParam = location.hash?.split('#')[1]
      console.log('queryCategoryParam', queryCategoryParam)
      if (queryCategoryParam && raffleType != queryCategoryParam) {
        setPRaffleType(queryCategoryParam as raffleUserConnectionType)
      }
    }, 10)
  }, [location])

  const { isLedger, visible, setVisible } = useWalletSignInModal()

  useEffect(() => {
    if (!visible) {
      toast.remove()
    }
  }, [visible])

  const { data: rafflesData, isFetching } = useUserRaffles({
    userWallet: walletFromRouter,
    raffleType: raffleType,
    page: page,
  })

  useEffect(() => {
    if (rafflesData) {
      const filtered = rafflesData.filter((r) => {
        for (const raffle of raffles) {
          if (r.id === raffle.id) {
            return false
          }
        }
        return true
      })

      const newArr = [...raffles, ...filtered]
      if (hasMore && newArr.length === raffles.length) {
        setHasMore(false)
      }

      setRaffles(newArr)
    }
  }, [rafflesData])

  useEffect(() => {
    setRaffles([])
    setPage(0)
    setHasMore(true)
  }, [isOwnProfile, raffleType])

  const [activeTab, setActiveTab] = useState('Raffles')

  return (
    <Box color='#232323 !important' fontFamily='Inter !important'>
      <Box
        height={['30rem']}
        w='100%'
        bgRepeat='no-repeat'
        bgSize='cover'
        background={
          !loading && user
            ? `linear-gradient( rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5) ), url('${
                user.bannerUrl ?? ''
              }')`
            : ''
        }
        bgPos='center top'
        backgroundSize='cover!important'
        px='1rem'
        py={['.5rem', '1rem', '2rem']}
      >
        <Header theme='transparent' />
      </Box>

      <Container maxWidth='1600px' mt='-15rem'>
        <ProfileCard variant='user' />
      </Container>

      <VStack
        maxW='100vw'
        overflowX={'hidden'}
        width='100%'
        px={0}
        color='#232323 !important'
        fontFamily='Inter !important'
        marginTop='5rem'
      >
        <Container maxW='1600px' px='1rem'>
          <ContentTabs
            tabs={[
              'Raffles',
              'Communities',
              'Rewards',
              'Stats',
              // 'My Rewards',
              /* 'Activities', */
            ]}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Box mt='50px'>
            {activeTab === 'Raffles' && (
              <Box w='100%'>
                <Text fontSize='1.5rem' fontWeight='600'>
                  Raffles
                </Text>
                <Stack
                  mt={3}
                  direction={['column', 'column', 'row', 'row']}
                  justify='space-between'
                >
                  <FilterButtons
                    selectedValue={raffleType}
                    onChange={(it) =>
                      setRaffleType(it as raffleUserConnectionType)
                    }
                    labels={raffleTypeLabels}
                  />
                </Stack>

                {raffles && raffles.length > 0 ? (
                  <InfiniteScroll
                    scrollThreshold={0.75}
                    dataLength={raffles.length}
                    next={() => setPage(page + 1)}
                    hasMore={hasMore}
                    loader={
                      <Center mt='3rem'>
                        <LoadingMoreRaffles />
                      </Center>
                    }
                    endMessage={
                      <Center mt='3rem'>
                        {!isFetching ? <Text></Text> : <LoadingMoreRaffles />}
                      </Center>
                    }
                  >
                    <RaffleCardList raffles={raffles} />
                  </InfiniteScroll>
                ) : isFetching && (raffles?.length ?? 0) < 1 ? (
                  <RaffleCardListSkeleton />
                ) : raffleType !== 'SAVED' ? (
                  <Center mt='3rem'>
                    <Text>No raffles available</Text>
                  </Center>
                ) : (
                  <></>
                )}

                {raffleType === 'SAVED' && isOwnProfile && (
                  <>
                    {(savedByUserRes?.data?.length ?? 0) > 0 ? (
                      <RaffleCardList raffles={savedByUserRes?.data} />
                    ) : (
                      <Center mt='3rem'>
                        <Text>No raffles available</Text>
                      </Center>
                    )}
                  </>
                )}
              </Box>
            )}
            {activeTab === 'Communities' && (
              <Box mt='3rem'>
                <Text fontWeight='600'>
                  {isOwnProfile ? 'Your' : ''} Communities
                </Text>
                <Flex flexWrap='wrap' mb='1rem'>
                  {(!userCommunitiesRes.data ||
                    userCommunitiesRes.data?.length < 1) &&
                    !loading && <Text>No communities available</Text>}
                  {userCommunitiesRes.data?.map((p) => (
                    <CommunityBox key={p.id} project={p}></CommunityBox>
                  ))}
                  {loading &&
                    [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <Skeleton
                        key={i}
                        w='13rem'
                        h='90px'
                        mr='1rem'
                        my='.5rem'
                      />
                    ))}
                </Flex>
                {isOwnProfile && (
                  <Text>
                    Communities are refreshed once a day based on your Discord
                    roles. You can also refresh when creating a raffle.
                  </Text>
                )}
              </Box>
            )}
            {activeTab === 'Stats' && <UserProfileStats></UserProfileStats>}
            {activeTab === 'Rewards' && <UserRewardsOverview user={user} />}

            {activeTab === 'Activities' && <>Activities</>}
          </Box>
          <Footer />
        </Container>
      </VStack>
    </Box>
  )
}
